import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useRouteParam } from "@/lib/use-route-param";
import {
  AdminActionsCard,
  AdvancedEntitlementCard,
  type AdminActionFormState,
  type AdminActionKind,
  type BillingDetail,
  type DurationChoice,
  type EntitlementFormState,
  type PendingBillingAction,
} from "@/features/admin-billing/admin-billing-actions";
import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DAY_MS = 24 * 60 * 60 * 1000;

export const Route = createFileRoute("/admin/billing/$orgId")({
  component: AdminBillingDetailPage,
});

function formatDate(value: number | null) {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleString();
}

function formatMode(value: string | null | undefined) {
  return value ? value.replace(/_/g, " ") : "-";
}

function addDays(value: number, days: number) {
  return value + days * DAY_MS;
}

function defaultReason(action: AdminActionKind, duration: DurationChoice) {
  const durationLabel = duration === "custom" ? "custom time" : `${duration} days`;
  switch (action) {
    case "trial":
      return `Admin granted ${durationLabel} trial`;
    case "free_time":
      return `Admin extended billing by ${durationLabel}`;
    case "free_forever":
      return "Admin granted free forever access";
  }
}

function toDateTimeLocalInput(value: number | null | undefined) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function parseDateInput(value: string) {
  if (!value.trim()) {
    return undefined;
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
}

function getDurationDays(duration: DurationChoice) {
  return duration === "custom" ? undefined : Number(duration);
}

function isExtendableSubscriptionStatus(status: string | undefined) {
  return status === "active" || status === "trialing";
}

function buildDefaultAdminAction(detail: BillingDetail): AdminActionFormState {
  const canExtendBilling =
    Boolean(detail.billing?.polarSubscriptionId) &&
    isExtendableSubscriptionStatus(detail.billing?.subscriptionStatus);
  const action = canExtendBilling
    ? "free_time"
    : detail.billing?.polarSubscriptionId
      ? "free_forever"
      : "trial";
  return {
    action,
    duration: "30",
    customEndsAt: toDateTimeLocalInput(addDays(detail.summary.renewalDate ?? Date.now(), 30)),
    seatChoice: "unlimited",
    seatLimit: "",
    reason: defaultReason(action, "30"),
  };
}

function buildInitialForm(detail: BillingDetail): EntitlementFormState {
  return {
    planKey: detail.entitlement?.planKey ?? "manual_override",
    billingMode: (detail.entitlement?.billingMode ?? "manual_free") as EntitlementFormState["billingMode"],
    accessStatus: (detail.entitlement?.accessStatus ?? "active") as EntitlementFormState["accessStatus"],
    seatPolicy: (detail.entitlement?.seatPolicy ?? "unlimited") as EntitlementFormState["seatPolicy"],
    seatLimit: detail.entitlement?.seatLimit ? String(detail.entitlement.seatLimit) : "",
    expiresAt: toDateTimeLocalInput(detail.entitlement?.expiresAt),
    graceEndsAt: toDateTimeLocalInput(detail.entitlement?.graceEndsAt),
    reason: detail.entitlement?.reason ?? "",
  };
}

function AdminBillingDetailPage() {
  const orgId = useRouteParam("orgId");
  const { data, isPending, error } = useQuery(
    convexQuery(
      api.admin.billing.getOrganizationBillingDetail,
      orgId ? { organizationId: orgId as Id<"organizations"> } : "skip"
    )
  );
  const detail = data as BillingDetail | undefined;
  const setEntitlement = useMutation(api.admin.billing.setOrganizationEntitlement);
  const restorePolarEntitlement = useMutation(api.admin.billing.restorePolarEntitlement);
  const reconcileOrganizationSeats = useAction(api.admin.billing.reconcileOrganizationSeats);
  const extendPolarBillingPeriod = useAction(api.admin.billing.extendPolarBillingPeriod);
  const [pendingAction, setPendingAction] = useState<PendingBillingAction>(null);
  const [form, setForm] = useState<EntitlementFormState | null>(null);
  const [adminAction, setAdminAction] = useState<AdminActionFormState | null>(null);

  useEffect(() => {
    if (detail) {
      setForm(buildInitialForm(detail));
      setAdminAction(buildDefaultAdminAction(detail));
    }
  }, [detail]);

  if (!orgId) {
    return <div className="py-24 text-center text-muted-foreground">Invalid billing route.</div>;
  }

  if (error) {
    return <div className="py-24 text-center text-muted-foreground">Failed to load billing details.</div>;
  }

  if (isPending || detail === undefined || form === null || adminAction === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleChange = <K extends keyof EntitlementFormState>(
    key: K,
    value: EntitlementFormState[K]
  ) => {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  const handleAdminActionChange = <K extends keyof AdminActionFormState>(
    key: K,
    value: AdminActionFormState[K]
  ) => {
    setAdminAction((current) => {
      if (!current) {
        return current;
      }
      const next = { ...current, [key]: value };
      if (key === "duration" && current.reason === defaultReason(current.action, current.duration)) {
        next.reason = defaultReason(current.action, value as DurationChoice);
      }
      return next;
    });
  };

  const handleSelectAdminAction = (action: AdminActionKind) => {
    setAdminAction((current) => {
      const duration = current?.duration ?? "30";
      return {
        ...(current ?? buildDefaultAdminAction(detail)),
        action,
        reason: defaultReason(action, duration),
      };
    });
  };

  const getSeatGrant = () => {
    if (adminAction.seatChoice === "unlimited") {
      return { seatPolicy: "unlimited" as const, seatLimit: undefined };
    }
    const seatLimit = Number(adminAction.seatLimit);
    if (!Number.isInteger(seatLimit) || seatLimit <= 0) {
      throw new Error("Enter a positive whole number of seats");
    }
    return { seatPolicy: "fixed_limit" as const, seatLimit };
  };

  const getManualGrantExpiration = () => {
    const durationDays = getDurationDays(adminAction.duration);
    if (durationDays !== undefined) {
      return addDays(Date.now(), durationDays);
    }
    const expiresAt = parseDateInput(adminAction.customEndsAt);
    if (!expiresAt) {
      throw new Error("Choose when the grant should end");
    }
    if (expiresAt <= Date.now()) {
      throw new Error("Grant end date must be in the future");
    }
    return expiresAt;
  };

  const handleApplyAdminAction = async () => {
    setPendingAction("adminAction");
    try {
      const reason = adminAction.reason.trim() || defaultReason(adminAction.action, adminAction.duration);
      if (adminAction.action === "free_time") {
        if (!detail.billing?.polarSubscriptionId) {
          throw new Error("Free time requires an active Polar subscription. Use a trial for orgs without billing.");
        }
        if (!isExtendableSubscriptionStatus(detail.billing.subscriptionStatus)) {
          throw new Error("Free time requires an active Polar subscription.");
        }
        const durationDays = getDurationDays(adminAction.duration);
        const newPeriodEnd = durationDays === undefined ? parseDateInput(adminAction.customEndsAt) : undefined;
        if (durationDays === undefined && !newPeriodEnd) {
          throw new Error("Choose the new billing date");
        }
        await extendPolarBillingPeriod({
          organizationId: orgId as Id<"organizations">,
          durationDays,
          newPeriodEnd,
          reason,
        });
        toast.success("Polar billing period extended");
        return;
      }

      const seatGrant = getSeatGrant();
      await setEntitlement({
        organizationId: orgId as Id<"organizations">,
        planKey: adminAction.action === "trial" ? "trial" : "manual_free",
        billingMode: adminAction.action === "trial" ? "trial" : "internal",
        accessStatus: "active",
        seatPolicy: seatGrant.seatPolicy,
        seatLimit: seatGrant.seatLimit,
        expiresAt: adminAction.action === "trial" ? getManualGrantExpiration() : undefined,
        reason,
      });
      toast.success(adminAction.action === "trial" ? "Free trial started" : "Free forever access granted");
    } catch (actionError) {
      toast.error(actionError instanceof Error ? actionError.message : "Failed to apply billing action");
    } finally {
      setPendingAction(null);
    }
  };

  const handleSave = async () => {
    setPendingAction("save");
    try {
      await setEntitlement({
        organizationId: orgId as Id<"organizations">,
        planKey: form.planKey,
        billingMode: form.billingMode,
        accessStatus: form.accessStatus,
        seatPolicy: form.seatPolicy,
        seatLimit: form.seatLimit.trim() ? Number(form.seatLimit) : undefined,
        expiresAt: parseDateInput(form.expiresAt),
        graceEndsAt: parseDateInput(form.graceEndsAt),
        reason: form.reason.trim() || "Manual entitlement update",
      });
      toast.success("Entitlement updated");
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : "Failed to update entitlement");
    } finally {
      setPendingAction(null);
    }
  };

  const handleRestorePolar = async () => {
    setPendingAction("restore");
    try {
      await restorePolarEntitlement({
        organizationId: orgId as Id<"organizations">,
        reason: "Restore Polar-managed entitlement",
      });
      toast.success("Polar entitlement restored");
    } catch (restoreError) {
      toast.error(
        restoreError instanceof Error ? restoreError.message : "Failed to restore Polar entitlement"
      );
    } finally {
      setPendingAction(null);
    }
  };

  const handleReconcile = async () => {
    setPendingAction("reconcile");
    try {
      const result = await reconcileOrganizationSeats({
        organizationId: orgId as Id<"organizations">,
      });
      if (result.synced) {
        toast.success(`Reconciled ${result.seatCount} seats`);
      } else {
        toast.error(result.reason ?? "No Polar subscription found");
      }
    } catch (reconcileError) {
      toast.error(
        reconcileError instanceof Error ? reconcileError.message : "Failed to reconcile seats"
      );
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={detail.organization.name}
        description="Platform billing administration"
        breadcrumbs={[
          { label: "Admin", href: "/admin" },
          { label: "Billing", href: "/admin/billing" },
          { label: detail.organization.name },
        ]}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <SummaryStat label="Billing mode" value={formatMode(detail.summary.billingMode)} />
        <SummaryStat label="Access" value={detail.summary.accessStatus} />
        <SummaryStat label="Billed vs active" value={`${detail.summary.billedSeats ?? 0} / ${detail.summary.activeMembers}`} />
        <SummaryStat label="Seat status" value={formatMode(detail.summary.seatSyncStatus)} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <BillingStateCard detail={detail} />
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Reconciliation</CardTitle>
            <CardDescription>
              Seat counts are managed from active organization members in the app, not from Polar member assignments.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Current seat delta: {detail.summary.seatDelta ?? 0}. All users keep access while reconciliation is pending.
            </p>
            <Button onClick={() => void handleReconcile()} disabled={pendingAction !== null}>
              {pendingAction === "reconcile" && <Loader2 className="size-4 animate-spin" />}
              Reconcile seats
            </Button>
          </CardContent>
        </Card>
      </div>

      <AdminActionsCard
        detail={detail}
        form={adminAction}
        pendingAction={pendingAction}
        onSelectAction={handleSelectAdminAction}
        onChange={handleAdminActionChange}
        onApply={() => void handleApplyAdminAction()}
        onRestorePolar={() => void handleRestorePolar()}
      />

      <AdvancedEntitlementCard
        form={form}
        pendingAction={pendingAction}
        onChange={handleChange}
        onSave={() => void handleSave()}
        onRestorePolar={() => void handleRestorePolar()}
        canRestorePolar={Boolean(detail.billing?.polarSubscriptionId)}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <MembersCard members={detail.activeMembers} />
        <WebhookEventsCard events={detail.webhookEvents} />
      </div>
    </div>
  );
}

function SummaryStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-lg border-2 border-border bg-card px-6 py-4">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {props.label}
      </p>
      <p className="mt-1 text-2xl font-bold tracking-tight text-foreground">{props.value}</p>
    </div>
  );
}

function BillingStateCard(props: { detail: BillingDetail }) {
  const { detail } = props;
  return (
    <Card className="py-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Billing State</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <InfoRow label="Provider" value={detail.summary.provider ?? "-"} />
        <InfoRow label="Billing status" value={detail.summary.billingStatus ?? "-"} />
        <InfoRow label="Plan key" value={detail.summary.planKey ?? "-"} />
        <InfoRow label="Effective source" value={formatMode(detail.summary.accessSource)} />
        <InfoRow label="Entitlement status" value={detail.summary.entitlementLifecycleStatus} />
        <InfoRow label="Entitlement expiry" value={formatDate(detail.summary.expiresAt)} />
        <InfoRow label="Seat policy" value={formatMode(detail.summary.seatPolicy)} />
        <InfoRow label="Seat limit" value={detail.summary.seatLimit === null ? "-" : String(detail.summary.seatLimit)} />
        <InfoRow label="Renewal" value={formatDate(detail.summary.renewalDate)} />
        <InfoRow label="Last webhook" value={formatDate(detail.summary.lastWebhookAt)} />
        <InfoRow label="Last seat sync" value={formatDate(detail.summary.lastSeatSyncAt)} />
        <InfoRow label="Reason" value={detail.summary.reason ?? "-"} />
        {detail.summary.requiresReconciliation ? (
          <Badge variant="destructive">Needs reconciliation</Badge>
        ) : (
          <Badge variant="outline">{formatMode(detail.summary.seatSyncStatus)}</Badge>
        )}
      </CardContent>
    </Card>
  );
}

function InfoRow(props: { label: string; value: string }) {
  return (
    <p>
      <span className="text-muted-foreground">{props.label}:</span> {props.value}
    </p>
  );
}

function MembersCard(props: {
  members: Array<{ _id: string; email: string; name: string | null; orgRole: string }>;
}) {
  return (
    <Card className="py-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Active Members</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border-2 border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {props.members.map((member) => (
                <TableRow key={member._id}>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.name ?? "-"}</TableCell>
                  <TableCell>{member.orgRole}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function WebhookEventsCard(props: {
  events: Array<{ _id: string; eventType: string; occurredAt: number; processedAt: number }>;
}) {
  return (
    <Card className="py-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Recent Webhooks</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border-2 border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Occurred</TableHead>
                <TableHead>Processed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {props.events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                    No webhook events yet.
                  </TableCell>
                </TableRow>
              ) : (
                props.events.map((event) => (
                  <TableRow key={event._id}>
                    <TableCell>{event.eventType}</TableCell>
                    <TableCell>{formatDate(event.occurredAt)}</TableCell>
                    <TableCell>{formatDate(event.processedAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
